// PURPOSE: Profile picture editing and private membership avatars.
// CONTRACT: Spec 068 B8; explicit upload, versioned membership writes, initials on unavailable images.
// DEPENDS: Session, attachment download route and shared initials presentation.
// CEILING: No provider, bucket or signed URL access.
import './profile.css';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from './auth';
import { ApiError, newIdempotencyKey } from './api';
import { ErrorNotice } from './components';
import { PersonAvatar } from './person-avatar';

type Avatar = {version:number; avatar_attachment_id:string|null};
const limit=2*1024*1024;
export function MemberAvatar({id,name}:{id:string;name:string}) {
 const {api,me}=useSession();
 const profile=useQuery({queryKey:['avatar',me.tenant_id,id],queryFn:()=>api.request<Avatar>(`/members/${encodeURIComponent(id)}/avatar`),staleTime:60_000,retry:false});
 const attachment=profile.data?.avatar_attachment_id;
 const bytes=useQuery({queryKey:['avatar-bytes',me.tenant_id,attachment],queryFn:()=>api.attachmentBlob(attachment!),enabled:!!attachment,staleTime:60_000,retry:false});
 const [picture,setPicture]=useState<{blob:Blob;url:string}>();
 useEffect(()=>{
  if(!bytes.data)return;
  const url=URL.createObjectURL(bytes.data);setPicture({blob:bytes.data,url});
  return ()=>URL.revokeObjectURL(url);
 },[bytes.data]);
 return <PersonAvatar id={id} name={name} picture={attachment && !profile.isError && !bytes.isError && picture?.blob===bytes.data ? picture?.url : undefined}/>;
}

function encoded(file:File):Promise<string> {
 return new Promise((resolve,reject)=>{
  const reader=new FileReader();reader.onerror=()=>reject(new Error('The image could not be read.'));
  reader.onload=()=>resolve(String(reader.result).split(',')[1]);reader.readAsDataURL(file);
 });
}

export function ProfileAvatar() {
 const {api,me}=useSession();const cache=useQueryClient();
 const profile=useQuery({queryKey:['avatar',me.tenant_id,me.id],queryFn:()=>api.request<Avatar>('/profile/avatar'),retry:false});
 const input=useRef<HTMLInputElement>(null);
 const attempt=useRef<{file:File;version:number;key:string} | undefined>(undefined);
 const [file,setFile]=useState<File>();const [error,setError]=useState<unknown>();const [busy,setBusy]=useState(false);const [saved,setSaved]=useState(false);
 async function save() {
  if(!file || !profile.data || busy)return;
  setBusy(true);setError(undefined);setSaved(false);
  try {
   if(attempt.current?.file!==file)attempt.current={file,version:profile.data.version,key:newIdempotencyKey()};
   const result=await api.request<Avatar>('/profile/avatar',{method:'POST',body:JSON.stringify({version:attempt.current.version,idempotency_key:attempt.current.key,filename:file.name,content_type:file.type,data:await encoded(file)})});
   cache.setQueryData(['avatar',me.tenant_id,me.id],result);setFile(undefined);if(input.current)input.current.value='';setSaved(true);
   await cache.invalidateQueries({queryKey:['members']});
  } catch(failure) {
   setError(failure);
   if(failure instanceof ApiError && failure.stale){attempt.current=undefined;await profile.refetch();}
  } finally {setBusy(false);}
 }
 return <section className="panel"><div className="panel-heading"><div><h2>Profile picture</h2><p>Choose a raster image up to 2 MB for your workspace picture.</p></div></div>
  <form className="profile-picture-form" onSubmit={event=>{event.preventDefault();void save();}}><label className="button secondary profile-file-choice">Choose image…<input ref={input} aria-label="Profile picture" type="file" accept="image/*" disabled={busy} onChange={event=>{
   setSaved(false);setError(undefined);const selected=event.target.files?.[0];
   if(selected && (!selected.type.startsWith('image/') || selected.size>limit || !selected.size)){setFile(undefined);setError(new Error('Choose an image no larger than 2 MB.'));return;}
   setFile(selected);
  }}/></label><span className="profile-file-name" role="status">{file ? `${file.name} · ${(file.size/1024).toFixed(1)} KB` : 'No image selected'}</span><button className="button primary" disabled={busy || !file || !profile.data?.version}>{busy ? 'Saving…' : 'Save picture'}</button></form>
  {saved && <p role="status">Picture saved.</p>}{!!(error || profile.error) && <ErrorNotice error={error || profile.error} retry={()=>profile.refetch()}/>}</section>;
}
